import React, { ReactNode } from 'react'
import { Box, Button as ButtonBase, Stack, Tooltip, Typography } from '@mui/material'
import { Control, Controller, FieldValues, useFormContext } from 'react-hook-form'

import BaseCheckbox from 'packages/ui/Checkbox'

export interface IRoleItemCheckboxProps {
  role: string
  locked?: boolean
  labelColor?: string
  hidden: boolean
  label?: string
  checked: boolean
  value?: string
  tooltip?: string
  icon?: JSX.Element
  padding: string
  control: Control<FieldValues>
}

const Label = ({
  value,
  labelColor,
  icon,
  label,
  tooltip
}) => (
  <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
    {Boolean(icon) && (
      <Box
        mr={1}
        sx={{
          color: value ? labelColor || '#464646' : '#989898',
          minWidth: 'auto'
        }}>
        {icon}
      </Box>
    )}
    <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
      <Typography
        component="span"
        sx={{
          color: value ? labelColor || '#464646' : '#989898',
          textAlign: 'left',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: 1.2,
          wordBreak: 'break-word'
        }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right" arrow>
          <i
            className="fa-regular fa-circle-info"
            style={{
              color: value ? '#7f93a4' : '#D1D5DB',
              fontSize: '12px',
              flexShrink: 0
            }}
          />
        </Tooltip>
      )}
    </Box>
  </Stack>
)

const Button = ({
  checked,
  label,
  labelColor,
  icon,
  tooltip,
  padding,
  onClick = () => {},
  children
}) => {
  return (
    <ButtonBase
      disableRipple
      sx={{
        minHeight: '44px',
        px: padding,
        justifyContent: 'space-between',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: 'white !important'
        }
      }}
      onClick={onClick}
    >
      <Label
        value={checked}
        labelColor={labelColor}
        label={label}
        icon={icon}
        tooltip={tooltip}
      />
      <Box sx={{ minWidth: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
        {children}
      </Box>
    </ButtonBase>
  )
}

interface ICheckbox {
  checked: boolean
  label?: string
  labelColor?: string
  icon: string | ReactNode
  locked: boolean
  tooltip?: string
  onChange: (v:boolean) => void
  padding: string
}

export const Checkbox: React.FC<ICheckbox> = ({
  checked,
  label,
  labelColor,
  icon,
  locked = false,
  tooltip,
  onChange = () => {},
  padding
}) => {
  return (
    <Button
      checked={checked}
      labelColor={labelColor}
      label={label}
      icon={icon}
      tooltip={tooltip}
      padding={padding}
      onClick={() => onChange(!checked)}
    >
      <BaseCheckbox
        checked={checked}
        onChange={() => {
          onChange(!checked)
        }}
        disabled={locked}
      />
    </Button>
  )
}

const RoleItemCheckbox = ({
  role,
  checked,
  label,
  labelColor,
  icon,
  locked = false,
  tooltip,
  padding = '16px',
}: IRoleItemCheckboxProps) => {
  const {control} = useFormContext()
  return (
    <Controller
      key={role}
      control={control}
      name={`roles.${role}`}
      defaultValue={checked}
      render={({ field: { onChange, value } }) => (
        <Checkbox
          padding={padding}
          checked={value}
          labelColor={labelColor}
          label={label}
          icon={icon}
          tooltip={tooltip}
          locked={locked}
          onChange={onChange}
        />
      )}
    />
  )
}

export default RoleItemCheckbox